import { useEffect, useRef, useState } from "react";
import type { CreateWorkflowField, Field } from "~/types/api";
import { areFieldsEqual } from "../utils/field-equality";
import { isFieldType, type FieldUnion } from "../utils/field-type-guards";

type UseMetadataFieldsProps = {
  isEditMode: boolean;
  apiFields: Field[];
  workflowFields: FieldUnion[];
};

const getNextTemporaryFieldId = (fields: FieldUnion[]) => {
  const lowestId = fields.reduce((lowest, field) => {
    const fieldId = "id" in field && typeof field.id === "number" ? field.id : 0;
    return fieldId < lowest ? fieldId : lowest;
  }, 0);

  return lowestId - 1;
};

export const useMetadataFields = ({
  isEditMode,
  apiFields,
  workflowFields,
}: UseMetadataFieldsProps) => {
  const hasInitializedRef = useRef(false);

  const [localFields, setLocalFields] = useState<FieldUnion[]>(() =>
    workflowFields.length > 0 ? workflowFields : []
  );
  const [originalFields, setOriginalFields] = useState<Map<number, Field>>(new Map());
  const [mutatedFieldIds, setMutatedFieldIds] = useState<Set<number>>(new Set());
  const [initialFieldsSnapshot] = useState<FieldUnion[]>(() =>
    workflowFields.length > 0 ? workflowFields : []
  );

  useEffect(() => {
    if (
      isEditMode &&
      apiFields.length > 0 &&
      localFields.length === 0 &&
      !hasInitializedRef.current
    ) {
      const fieldsMap = new Map<number, Field>();
      apiFields.forEach((field) => {
        fieldsMap.set(field.id, { ...field, options: field.options ? [...field.options] : [] });
      });
      setOriginalFields(fieldsMap);
      setLocalFields(apiFields as FieldUnion[]);
      hasInitializedRef.current = true;
    }
  }, [isEditMode, apiFields, localFields]);

  const updateMutationTracking = (field: FieldUnion) => {
    if (!isFieldType(field)) return;
    const original = originalFields.get(field.id);
    if (!original) return;

    if (areFieldsEqual(field, original)) {
      setMutatedFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(field.id);
        return next;
      });
    } else {
      setMutatedFieldIds((prev) => new Set(prev).add(field.id));
    }
  };

  const addField = () => {
    const newField: CreateWorkflowField = {
      id: getNextTemporaryFieldId(localFields),
      name: `Field ${localFields.length + 1}`,
      type: "String",
      required: false,
      options: [],
      order: localFields.length,
    };
    setLocalFields((prev) => [...prev, newField]);
    return localFields.length; // new index
  };

  const updateFieldAtIndex = (index: number, updatedField: FieldUnion) => {
    setLocalFields((prev) => {
      const next = [...prev];
      next[index] = updatedField;
      return next;
    });
  };

  const deleteFieldAtIndex = (index: number, orderedFields: FieldUnion[]) => {
    const fieldToDelete = orderedFields[index];
    if (!fieldToDelete) return;

    const deletedOrder = fieldToDelete.order || 0;

    if (isFieldType(fieldToDelete)) {
      setMutatedFieldIds((prev) => new Set(prev).add(fieldToDelete.id));
    }

    const newList = orderedFields
      .filter((_, i) => i !== index)
      .map((field) => {
        const currentOrder = field.order || 0;
        if (currentOrder > deletedOrder) {
          const updated = { ...field, order: currentOrder - 1 };
          updateMutationTracking(updated);
          return updated;
        }
        return field;
      });

    setLocalFields(newList);
  };

  const reorderFields = (reorderedFields: FieldUnion[]) => {
    if (isEditMode) {
      reorderedFields.forEach(updateMutationTracking);
    }
    setLocalFields(reorderedFields);
  };

  const resetMutations = () => setMutatedFieldIds(new Set());

  const resetLocalFields = () => {
    if (isEditMode) {
      setLocalFields(Array.from(originalFields.values()));
      setMutatedFieldIds(new Set());
    } else {
      setLocalFields(initialFieldsSnapshot);
    }
  };

  return {
    localFields,
    originalFields,
    mutatedFieldIds,
    initialFieldsSnapshot,
    addField,
    updateFieldAtIndex,
    deleteFieldAtIndex,
    reorderFields,
    updateMutationTracking,
    resetMutations,
    resetLocalFields,
  };
};
